import { useEffect, useMemo, useState } from "react";
import type { CallRing, DirectChannel, Uuid } from "@shared/api";
import { fromPublic, Tile } from "@entities/user";
import { Hint } from "@shared/ui/bits";
import { IconCall, IconClose } from "@shared/ui/icons";
import { directName, isGroup } from "../lib/title";
import { liveRings } from "../model/rings";
import { useRingtone } from "../model/ringtone";
import { GroupTile } from "./Group";

/**
 * Входящий звонок — как в Discord: окно посреди экрана с лицом звонящего,
 * «Входящий звонок…» и двумя кнопками, под мелодию вызова.
 *
 * Звонит, пока сервер держит вызов (`rings`), а не пока в переписке кто-то
 * есть: вышедшему из звонка заново не звонят, он входит сам. Отклонить —
 * у звонящего пропадает вызов, у нас — окно и мелодия.
 *
 * Открыта сама переписка — окна нет: там полоса звонка со своими «принять» и
 * «отклонить». Мелодия при этом играет: звонок есть, где бы ни смотрели.
 */
export function IncomingCall({
  directs,
  rings,
  now,
  meId,
  callChannelId,
  openChannelId,
  onAccept,
  onDecline,
}: {
  directs: DirectChannel[];
  rings: Record<Uuid, CallRing[]>;
  /** Часы вызовов (`useRingClock`): вызов гаснет сам по сроку. */
  now: number;
  meId: Uuid;
  /** Где идёт наш звонок. */
  callChannelId: Uuid | null;
  /** Какая переписка открыта посреди окна. */
  openChannelId: Uuid | null;
  onAccept: (channelId: Uuid) => void;
  onDecline: (channelId: Uuid) => void;
}) {
  // Отклонили — окно уходит сразу, не дожидаясь события от сервера.
  const [declined, setDeclined] = useState<ReadonlySet<Uuid>>(() => new Set());

  const ringing = useMemo(
    () =>
      directs.find(
        (direct) =>
          direct.channel_id !== callChannelId &&
          !declined.has(direct.channel_id) &&
          liveRings(rings[direct.channel_id], now).some((ring) => ring.user_id === meId),
      ) ?? null,
    [directs, rings, now, meId, callChannelId, declined],
  );

  // Сервер снял вызов — местный отказ больше не нужен: следующий вызов звонит.
  useEffect(() => {
    if (declined.size === 0) return;
    const still = [...declined].filter((id) => liveRings(rings[id], now).some((ring) => ring.user_id === meId));
    if (still.length !== declined.size) setDeclined(new Set(still));
  }, [rings, now, meId, declined]);

  useRingtone("incoming", !!ringing);

  const shown = ringing && ringing.channel_id !== openChannelId ? ringing : null;
  const decline = (channelId: Uuid) => {
    setDeclined((was) => new Set([...was, channelId]));
    onDecline(channelId);
  };

  // Как в Discord: Ctrl+Enter — принять, Esc — отклонить, пока окно на экране.
  useEffect(() => {
    if (!shown) return;
    const id = shown.channel_id;
    const key = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        onAccept(id);
      } else if (event.key === "Escape") {
        event.preventDefault();
        decline(id);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
    // Отклонение — своё состояние и колбэк хозяина: подписка по самому звонку.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown?.channel_id]);

  if (!shown) return null;
  const ringingNow = shown;

  return (
    <div className="incoming-call" role="alertdialog" aria-label={`Входящий звонок: ${directName(ringingNow)}`}>
      <span className="incoming-call-face">
        {isGroup(ringingNow) ? (
          <GroupTile id={ringingNow.channel_id} size={80} iconId={ringingNow.icon_id ?? null} />
        ) : (
          <Tile person={fromPublic(ringingNow.recipient)} size={80} mark={null} />
        )}
      </span>
      <span className="incoming-call-name ellipsis">{directName(ringingNow)}</span>
      <span className="incoming-call-note">Входящий звонок…</span>
      <span className="incoming-call-actions">
        <Hint label="Отклонить · Esc">
          <button
            type="button"
            className="call-answer decline"
            aria-label="Отклонить"
            onClick={() => decline(ringingNow.channel_id)}
          >
            <IconClose size={20} />
          </button>
        </Hint>
        <Hint label="Принять · Ctrl+Enter">
          <button
            type="button"
            className="call-answer accept"
            aria-label="Принять"
            onClick={() => onAccept(ringingNow.channel_id)}
          >
            <IconCall size={20} />
          </button>
        </Hint>
      </span>
    </div>
  );
}
